import axios from 'axios';
import { apiConfig } from '@/utils/constants';
import { AppLogger } from '@/logger';
import { getErrorMessage } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';
import type { MaintenanceWindowDto } from './maintenance';

const logger = new AppLogger('adminMaintenanceWindows state');

const windowsUrl = `${apiConfig.basePath}/api/maintenance/windows`;
const authHeaders = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

interface IAdminMaintenanceWindowsState {
  windows: MaintenanceWindowDto[];
  windowsLoading: boolean;
  windowsLoadError?: string;
  windowsSaving: boolean;
  windowsSaveError?: string;
}

const initialState = (): IAdminMaintenanceWindowsState => ({
  windows: [],
  windowsLoading: false,
  windowsLoadError: undefined,
  windowsSaving: false,
  windowsSaveError: undefined,
});

type ActionParams = IActionParams<IAdminMaintenanceWindowsState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    WINDOWS_LOADING(state: IAdminMaintenanceWindowsState) {
      state.windowsLoadError = undefined;
      state.windowsLoading = true;
    },
    WINDOWS_LOADED(state: IAdminMaintenanceWindowsState, payload: MaintenanceWindowDto[]) {
      state.windows = payload;
      state.windowsLoading = false;
    },
    WINDOWS_LOAD_ERROR(state: IAdminMaintenanceWindowsState, error: Error) {
      state.windowsLoadError = getErrorMessage(error);
      state.windowsLoading = false;
    },
    WINDOWS_SAVING(state: IAdminMaintenanceWindowsState) {
      state.windowsSaveError = undefined;
      state.windowsSaving = true;
    },
    WINDOWS_SAVED(state: IAdminMaintenanceWindowsState) {
      state.windowsSaveError = undefined;
      state.windowsSaving = false;
    },
    WINDOWS_SAVE_ERROR(state: IAdminMaintenanceWindowsState, error: Error) {
      state.windowsSaveError = getErrorMessage(error);
      state.windowsSaving = false;
    },
    RESET_STATE(state: IAdminMaintenanceWindowsState) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async loadWindows({ commit, rootGetters }: ActionParams) {
      try {
        const accessToken = rootGetters['login/accessToken'];
        commit('WINDOWS_LOADING');
        const { data } = await axios.get<MaintenanceWindowDto[]>(windowsUrl, authHeaders(accessToken));
        commit('WINDOWS_LOADED', data);
      } catch (error) {
        commit('WINDOWS_LOAD_ERROR', error);
        logger.captureStoreError('loadWindows', error);
      }
    },
    async createWindow(
      { commit, dispatch, rootGetters }: ActionParams,
      dto: { startsAtUtc: string; endsAtUtc: string; message?: string | null; lmMessage?: string | null },
    ) {
      const accessToken = rootGetters['login/accessToken'];
      commit('WINDOWS_SAVING');
      try {
        await axios.post(windowsUrl, dto, authHeaders(accessToken));
        commit('WINDOWS_SAVED');
        await dispatch('loadWindows');
        await dispatch('maintenance/poll', null, { root: true });
      } catch (error) {
        commit('WINDOWS_SAVE_ERROR', error);
        logger.captureStoreError('createWindow', error, dto);
      }
    },
    async cancelWindow({ commit, dispatch, rootGetters }: ActionParams, id: number) {
      const accessToken = rootGetters['login/accessToken'];
      commit('WINDOWS_SAVING');
      try {
        await axios.patch(`${windowsUrl}/${id}/cancel`, {}, authHeaders(accessToken));
        commit('WINDOWS_SAVED');
        await dispatch('loadWindows');
        await dispatch('maintenance/poll', null, { root: true });
      } catch (error) {
        commit('WINDOWS_SAVE_ERROR', error);
        logger.captureStoreError('cancelWindow', error, { id });
      }
    },
    resetState({ commit }: ActionParams) {
      commit('RESET_STATE');
    },
  },
  getters: {
    windows: (s: IAdminMaintenanceWindowsState) => s.windows,
    windowsLoading: (s: IAdminMaintenanceWindowsState) => s.windowsLoading,
    windowsLoadError: (s: IAdminMaintenanceWindowsState): string | undefined => s.windowsLoadError,
    windowsSaving: (s: IAdminMaintenanceWindowsState) => s.windowsSaving,
    windowsSaveError: (s: IAdminMaintenanceWindowsState): string | undefined => s.windowsSaveError,
  },
};
